/**
 * PromptStudioComposer — the composer panel.
 * Source tabs (badges): Écrire, Générer, Persona (when the brand has Brand Knowledge personas), Mots clés,
 * CSV, Search Console.
 *
 * Générer and Persona put their proposals in an EDITABLE draft under the band, then « Vérifier »
 * (owner, 2026-11-02: tweak the text before classification). Mots clés still goes straight to the
 * review via onVerifier.
 *
 * @see B-popup-frontend.md step 5/6, Files and props § Composer
 */
import { useState } from 'react';
import { Box, Button, Chip, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ListIcon from '@mui/icons-material/List';
import PersonIcon from '@mui/icons-material/Person';
import type { BrandQueryAxis, BrandPersona, Location, TargetZone } from '@traaker/shared';
import { promptPlaceKey, zoneDisplayLabel } from '@traaker/shared';
import type { ComposerSource, GenState, Drafts } from './composerModel';
import {
  draftLines,
  overCapLines,
} from './composerModel';
import type { LimitSlot, LocKey, PromptAllowanceFigures } from './promptStudioModel';
import { fitsMargin, MONDIAL, PROMPT_ZONE_EDITING, placeOptionsOf } from './promptStudioModel';
import { EcrireBand } from './EcrireBand';
import { CsvBand } from './CsvBand';
import { GenerateBand } from './GenerateBand';
import { PersonaBand } from './PersonaBand';
import { MotsClesBand } from './MotsClesBand';
import { SearchConsoleBand } from './SearchConsoleBand';
import { gscDraftLines } from './searchConsoleModel';
import { useGsc } from './searchConsoleStore';
import { LocChip } from './PromptRow';
import { Pick } from './Pick';

export interface ComposerState {
  source: ComposerSource;
  drafts: Drafts;
  gen: GenState;
  cooldownUntil: number | null;
  genDone: boolean;
  kwDone: boolean;
  kwDropped: number;
  keywords: string[];
  /** The batch place (`bulkLoc`, every draft line); null = Mondial. */
  zone: TargetZone | null;
}

export interface PromptStudioComposerProps {
  state: ComposerState;
  brandId: string;
  axes: readonly BrandQueryAxis[];
  vocabByAxis: Record<BrandQueryAxis, string[]>;
  languages: { code: string; label: string }[];
  personas: BrandPersona[] | null;
  figures: PromptAllowanceFigures | null;
  locations: Location[];
  /** The place picker's listed zones (promptPickerZones). */
  placeZones: Map<LocKey, TargetZone>;
  /** Place search language (brand working language). */
  lang: string;
  onChange: (next: Partial<ComposerState>) => void;
  onVerifier: (lines: string[], source: ComposerSource) => void;
  onCancel: () => void;
  /** i18n key+values, self-serve only */
  noRoomReason?: LimitSlot | null;
  /** Search Console (lot H): isAgency → its agency line; brandUrl feeds its « votre domaine » token. */
  searchConsole?: { isAgency: boolean; brandUrl?: string };
}

const SOURCE_ICONS: Record<ComposerSource, React.ReactNode> = {
  write: <EditIcon sx={{ fontSize: 14 }} />,
  gen: <AutoAwesomeIcon sx={{ fontSize: 14 }} />,
  persona: <PersonIcon sx={{ fontSize: 14 }} />,
  kw: <LocalOfferIcon sx={{ fontSize: 14 }} />,
  csv: <ListIcon sx={{ fontSize: 14 }} />,
  gsc: (
    <Box
      component="img"
      src="/icons/search-console.svg"
      alt="GSC"
      sx={{ width: 14, height: 14 }}
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  ),
};

// i18n labels
export const SOURCE_LABELS: Record<ComposerSource, string> = {
  write: 'Écrire',       // i18n: promptStudio.source.write
  gen: 'Générer',        // i18n: promptStudio.source.gen
  persona: 'Persona',    // i18n: promptStudio.source.persona
  kw: 'Mots clés',       // i18n: promptStudio.source.kw
  csv: 'CSV',            // i18n: promptStudio.source.csv
  gsc: 'Search Console', // i18n: promptStudio.source.gsc
};

export function PromptStudioComposer({
  state,
  brandId,
  axes,
  vocabByAxis,
  languages,
  personas,
  figures,
  locations: _locations,
  placeZones,
  lang,
  onChange,
  onVerifier,
  onCancel,
  noRoomReason,
  searchConsole,
}: PromptStudioComposerProps) {
  const { source, drafts, gen, cooldownUntil, genDone, kwDone, kwDropped, keywords, zone } = state;
  const [pickOpen, setPickOpen] = useState(false);
  const [placeQuery, setPlaceQuery] = useState('');
  const pickZones = placeOptionsOf([zone], placeZones);
  const pickZone = (z: TargetZone | null) => {
    onChange({ zone: z });
    setPickOpen(false);
  };

  // Search Console: the draft is the band's (the checked rows' proposals, editable; null = not
  // ready: no draft, no « Vérifier », only « Annuler »).
  const gscOn = source === 'gsc';
  const gscLines = gscDraftLines(useGsc(brandId));
  const gscReady = gscOn && gscLines !== null;

  const currentDraft = source === 'gsc' ? (gscLines ?? []).join('\n') : drafts[source] ?? '';
  const lines = draftLines(currentDraft);
  const capCount = overCapLines(lines);

  const margin = fitsMargin(figures);
  const noRoom = !!(noRoomReason ?? (margin !== null && margin.hi <= 0));
  // Générer / Persona: « Vérifier » once their draft has lines (the user may edit it first).
  const hasVerify = source === 'write' || source === 'csv' || gscReady
    || ((source === 'gen' || source === 'persona') && lines.length > 0);
  const hasPersonas = !!personas && personas.length > 0;

  const handleVerifier = () => {
    if (noRoom) return;
    const srcLines = source === 'gsc' ? gscLines ?? [] : draftLines(drafts[source] ?? '');
    onVerifier(srcLines, source);
  };

  // Générer / Persona: the proposals REPLACE that source's draft, editable before « Vérifier ».
  const handleGenDraft = (proposalLines: string[]) => {
    onChange({ drafts: { ...drafts, gen: proposalLines.join('\n') }, genDone: true });
  };
  const handlePersonaDraft = (proposalLines: string[]) => {
    onChange({ drafts: { ...drafts, persona: proposalLines.join('\n') } });
  };

  // Step 6: after proposals arrive from MotsClesBand, update state + go to review.
  const handleKwDraft = (proposalLines: string[], dropped: number) => {
    onChange({
      drafts: { ...drafts, kw: proposalLines.join('\n') },
      kwDone: true,
      kwDropped: dropped,
    });
    onVerifier(proposalLines, 'kw');
  };

  const handleCooldown = (until: number) => {
    onChange({ cooldownUntil: until });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Source badges */}
      <Box sx={{ display: 'flex', gap: 0.75, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, flexWrap: 'wrap' }}>
        {(['write', 'gen', 'persona', 'kw', 'csv', 'gsc'] as ComposerSource[]).filter((s) => s !== 'persona' || hasPersonas).map((s) => (
          <Chip
            key={s}
            icon={SOURCE_ICONS[s] as any}
            label={SOURCE_LABELS[s]}
            size="small"
            onClick={() => onChange({ source: s })}
            sx={{
              fontSize: 12,
              fontWeight: source === s ? 600 : 400,
              bgcolor: source === s ? 'rgba(124,58,237,.1)' : 'rgba(0,0,0,.05)',
              color: source === s ? '#7c3aed' : 'text.secondary',
              border: source === s ? '1px solid rgba(124,58,237,.3)' : '1px solid transparent',
              cursor: 'pointer',
            }}
          />
        ))}
      </Box>

      {/* Band content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
        {source === 'write' && (
          <EcrireBand
            draft={currentDraft}
            onChange={(d) => onChange({ drafts: { ...drafts, write: d } })}
          />
        )}
        {source === 'csv' && (
          <CsvBand
            onLoaded={(newLines) => {
              const prev = drafts.csv ?? '';
              const combined = prev ? `${prev}\n${newLines.join('\n')}` : newLines.join('\n');
              onChange({ drafts: { ...drafts, csv: combined } });
            }}
            onTemplate={() => {}}
          />
        )}
        {source === 'gen' && (
          <GenerateBand
            gen={gen}
            brandId={brandId}
            axes={axes}
            vocabByAxis={vocabByAxis}
            languages={languages}
            cooldownUntil={cooldownUntil}
            done={genDone}
            onChange={(g) => onChange({ gen: g })}
            onDraft={handleGenDraft}
            onCooldown={handleCooldown}
          />
        )}
        {source === 'persona' && hasPersonas && (
          <PersonaBand
            gen={gen}
            brandId={brandId}
            languages={languages}
            personas={personas!}
            cooldownUntil={cooldownUntil}
            done={!!drafts.persona}
            onChange={(g) => onChange({ gen: g })}
            onDraft={handlePersonaDraft}
            onCooldown={handleCooldown}
          />
        )}
        {/* The generated questions, editable before « Vérifier » (owner, 2026-11-02). */}
        {(source === 'gen' || source === 'persona') && lines.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <EcrireBand
              draft={currentDraft}
              onChange={(d) => onChange({ drafts: { ...drafts, [source]: d } })}
            />
          </Box>
        )}
        {source === 'kw' && (
          <MotsClesBand
            keywords={keywords}
            brandId={brandId}
            cooldownUntil={cooldownUntil}
            done={kwDone}
            dropped={kwDropped}
            onChange={(kws) => onChange({ keywords: kws })}
            onDraft={handleKwDraft}
            onCooldown={handleCooldown}
          />
        )}
        {source === 'gsc' && (
          <SearchConsoleBand
            brandId={brandId}
            brandUrl={searchConsole?.brandUrl}
            isAgency={searchConsole?.isAgency}
          />
        )}
      </Box>

      {/* Search Console not ready (flag on): only « Annuler » (UI-SPEC §2.12). */}
      {gscOn && !gscReady && (
        <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="text" size="small" onClick={onCancel}>
            {/* i18n: common.cancel */}
            Annuler
          </Button>
        </Box>
      )}

      {/* Footer — the batch place for every source but gsc (gsc once ready); Vérifier for write, csv
          and a ready gsc (gen/kw go to review via onDraft, so their foot is the place alone, chosen before the run). */}
      {(source !== 'gsc' || gscReady) && (
        <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          {/* Batch place (read-only Mondial while PROMPT_ZONE_EDITING is false) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: hasVerify ? 1 : 0 }}>
            <LocChip
              zone={PROMPT_ZONE_EDITING ? zone : null}
              onClick={PROMPT_ZONE_EDITING ? () => { setPlaceQuery(''); setPickOpen(true); } : undefined}
            />
          </Box>
          <Pick
            open={pickOpen}
            kind="place"
            selected={zone ? promptPlaceKey(zone) : MONDIAL}
            onToggle={(key) => pickZone(key === MONDIAL ? null : pickZones.get(key) ?? null)}
            onPickZone={pickZone}
            onClose={() => setPickOpen(false)}
            placeOptions={[...pickZones].map(([key, z]) => ({ key, zone: z, label: zoneDisplayLabel(z) }))}
            searchQuery={placeQuery}
            onSearchChange={setPlaceQuery}
            lang={lang}
          />

          {hasVerify && (
            <>
              {/* Margin / ratio */}
              {margin !== null && (
                <Box sx={{ fontSize: 12, color: margin.hi <= 0 ? '#b45309' : 'text.secondary', mb: 0.75 }}>
                  {/* i18n: promptStudio.composer.ratio / ratioOne / lines */}
                  {margin.lo === margin.hi
                    ? `${lines.length} / ${margin.lo}`
                    : `${lines.length} / ${margin.lo} à ${margin.hi}`}
                  {margin.hi <= 0 && (
                    <Box component="span" sx={{ ml: 1, color: '#b45309', fontWeight: 600 }}>
                      {/* i18n: promptStudio.cost.budgetReached */}
                      Budget atteint
                    </Box>
                  )}
                </Box>
              )}

              {capCount > 0 && (
                <Box sx={{ fontSize: 12, color: '#b45309', mb: 0.5 }}>
                  {/* i18n: promptStudio.draft.overCap */}
                  {capCount} ligne{capCount > 1 ? 's dépassent' : ' dépasse'} 300 caractères
                </Box>
              )}

              {noRoom && noRoomReason && (
                <Box sx={{ fontSize: 11.5, color: '#b45309', mb: 0.5 }}>
                  {noRoomReason.key}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button variant="text" size="small" onClick={onCancel}>
                  {/* i18n: common.cancel */}
                  Annuler
                </Button>
                <Tooltip title={noRoom ? (noRoomReason?.key ?? 'Budget atteint') : ''} arrow>
                  <Box component="span">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleVerifier}
                      disabled={noRoom || lines.length === 0}
                      data-ps-control="verifier"
                    >
                      {/* i18n: promptStudio.composer.verify */}
                      Vérifier
                    </Button>
                  </Box>
                </Tooltip>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
